import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft, FiEdit2, FiSend, FiXCircle, FiCopy, FiDownload, FiPrinter, FiTrash2, FiAlertTriangle, FiClock,
  FiRotateCcw,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, Button, ConfirmDialog, Spinner } from '../../components/common';
import InvoicePdfFrame from './components/InvoicePdfFrame';
import ProformaTotalsPanel from './components/ProformaTotalsPanel';
import CancelInvoiceModal from './components/CancelInvoiceModal';
import { proformaInvoiceService } from '../../services/proformaInvoiceService';
import { billingFiles } from '../../services/billingFiles';
import { getApiError } from '../../services/cmsService';
import { formatDate, getRelativeTime } from '../../utils/dateUtils';
import { InvoiceStatusBadge, PI_CANCEL_DESCRIPTION, formatDMY, formatINR } from './billingUtils';
import './Billing.css';
import './InvoiceDetailPage.css';

const ProformaInvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [pi, setPi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfVersion, setPdfVersion] = useState(0);
  const [busy, setBusy] = useState(null);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [confirmIssue, setConfirmIssue] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmReinstate, setConfirmReinstate] = useState(false);

  const load = useCallback(async () => {
    try {
      setPi(await proformaInvoiceService.getById(id));
    } catch (error) {
      toast.error(getApiError(error, 'Failed to load the proforma invoice'));
      navigate('/billing/proforma-invoices', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  /**
   * Mutation responses carry the new figures but not the activity log or the
   * download filename, so take them at once and then reload the full detail.
   * The server regenerates the PDF, so refetch that too.
   */
  const afterChange = (updated) => {
    setPi((prev) => ({ ...prev, ...updated, activity: prev.activity, downloadFilename: prev.downloadFilename, client: prev.client }));
    setPdfVersion((v) => v + 1);
    load();
  };

  const run = async (key, action) => {
    setBusy(key);
    try {
      await action();
    } catch (error) {
      toast.error(getApiError(error, 'Something went wrong'));
    } finally {
      setBusy(null);
    }
  };

  const doIssue = () =>
    run('issue', async () => {
      const issued = await proformaInvoiceService.issue(pi.id);
      toast.success(`Proforma invoice ${issued.number} issued`);
      afterChange(issued);
    });

  const doDuplicate = () =>
    run('duplicate', async () => {
      const copy = await proformaInvoiceService.duplicate(pi.id);
      toast.success('Duplicate draft created');
      navigate(`/billing/proforma-invoices/${copy.id}/edit`);
    });

  const doReinstate = () =>
    run('reinstate', async () => {
      const reinstated = await proformaInvoiceService.reinstate(pi.id);
      toast.success(`Proforma invoice ${reinstated.number} reinstated`);
      afterChange(reinstated);
    });

  const doDelete = () =>
    run('delete', async () => {
      await proformaInvoiceService.delete(pi.id);
      toast.success(pi.status === 'draft' ? 'Draft deleted' : `Proforma invoice ${pi.number} deleted`);
      navigate('/billing/proforma-invoices');
    });

  const download = () => run('download', () => billingFiles.downloadProformaPdf(pi.id, pi.downloadFilename));
  const print = () => run('print', () => billingFiles.printProformaPdf(pi.id));

  if (loading || !pi) {
    return (
      <div className="bl-center">
        <Spinner size="large" />
      </div>
    );
  }

  const { permissions } = pi;
  const isDraft = pi.status === 'draft';
  const isCancelled = pi.status === 'cancelled';
  const references = [
    ['Terms of payment', pi.paymentTerms],
    ['Terms of delivery', pi.deliveryTerms],
    ['Reference', pi.referenceNo],
    ['Buyer’s order', [pi.buyerOrderNo, pi.buyerOrderDate && formatDMY(pi.buyerOrderDate)].filter(Boolean).join(' · ')],
  ].filter(([, value]) => value);

  return (
    <div className="bl-page invoice-detail">
      <Link to="/billing/proforma-invoices" className="bl-back">
        <FiArrowLeft /> Back to proforma invoices
      </Link>

      <div className="id-header">
        <div className="id-title">
          <h1>{pi.number || 'Draft proforma invoice'}</h1>
          <div className="id-title-meta">
            <InvoiceStatusBadge status={pi.status} size="medium" />
            {pi.clientId ? (
              <Link to={`/billing/clients/${pi.clientId}`} className="bl-link">{pi.clientName}</Link>
            ) : (
              <span>{pi.clientName}</span>
            )}
          </div>
        </div>

        <div className="bl-header-actions">
          {permissions.canEdit && (
            <Button variant="outline" icon={<FiEdit2 />} onClick={() => navigate(`/billing/proforma-invoices/${pi.id}/edit`)}>
              Edit
            </Button>
          )}
          {permissions.canIssue && (
            <Button icon={<FiSend />} onClick={() => setConfirmIssue(true)} loading={busy === 'issue'}>
              Issue
            </Button>
          )}
          <Button variant="outline" icon={<FiDownload />} onClick={download} loading={busy === 'download'}>
            Download
          </Button>
          <Button variant="outline" icon={<FiPrinter />} onClick={print} loading={busy === 'print'}>
            Print
          </Button>
          <Button variant="ghost" icon={<FiCopy />} onClick={doDuplicate} loading={busy === 'duplicate'}>
            Duplicate
          </Button>
          {permissions.canCancel && (
            <Button variant="ghost" icon={<FiXCircle />} onClick={() => setCancelOpen(true)}>
              Cancel PI
            </Button>
          )}
          {permissions.canReinstate && (
            <Button variant="outline" icon={<FiRotateCcw />} onClick={() => setConfirmReinstate(true)} loading={busy === 'reinstate'}>
              Reinstate
            </Button>
          )}
          {permissions.canDelete && (
            <Button variant="ghost" icon={<FiTrash2 />} onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          )}
        </div>
      </div>

      {isCancelled && (
        <div className="bl-banner danger" role="status">
          <FiXCircle />
          <div className="bl-banner-body">
            <strong>Cancelled{pi.cancelledAt ? ` on ${formatDate(pi.cancelledAt)}` : ''}.</strong>{' '}
            {pi.cancelReason && <>Reason: {pi.cancelReason}</>}
          </div>
        </div>
      )}

      {isDraft && !pi.taxType && (
        <div className="bl-banner warning">
          <FiAlertTriangle />
          <div className="bl-banner-body">
            Client state unknown — tax is not applied and the draft can’t be issued.{' '}
            {pi.clientId && <Link to={`/billing/clients/${pi.clientId}`}>Edit client</Link>}
          </div>
        </div>
      )}

      {pi.client?.isDeleted && (
        <div className="bl-banner info">
          <FiAlertTriangle />
          <div className="bl-banner-body">This client has since been deleted. The PI keeps its own copy of the client details.</div>
        </div>
      )}

      <div className="id-layout">
        <Card padding="small" className="id-pdf-card">
          <InvoicePdfFrame kind="proforma" invoiceId={pi.id} version={pdfVersion} />
        </Card>

        <div className="id-side">
          <Card title="Summary" padding="medium">
            <dl className="bl-info-grid id-meta">
              <div>
                <dt>PI date</dt>
                <dd>{formatDMY(pi.piDate)}</dd>
              </div>
              <div>
                <dt>Lines</dt>
                <dd>{pi.itemsCount}</dd>
              </div>
              <div>
                <dt>GSTIN</dt>
                <dd className="bl-mono">{pi.clientGstin || '—'}</dd>
              </div>
              <div>
                <dt>Client ID</dt>
                <dd className="bl-mono">{pi.clientCode || '—'}</dd>
              </div>
              {references.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
            <div className="id-totals">
              <ProformaTotalsPanel totals={{ ...pi, halfRate: null }} unit={pi.unit} />
            </div>
            {pi.notes && (
              <p className="bl-muted" style={{ marginTop: '1rem' }}>
                <strong>Internal notes:</strong> {pi.notes}
              </p>
            )}
          </Card>

          <Card title="History" padding="medium">
            {pi.activity.length === 0 ? (
              <div className="bl-empty">No activity yet.</div>
            ) : (
              <ol className="id-timeline">
                {[...pi.activity].reverse().map((entry, index) => (
                  <li key={`${entry.action}-${entry.createdAt}-${index}`} className={`id-event is-${entry.action}`}>
                    <span className="id-event-dot" aria-hidden="true" />
                    <div className="id-event-body">
                      <span className="id-event-text">{entry.description}</span>
                      <span className="bl-cell-sub">
                        {entry.user && <>{entry.user} · </>}
                        <span title={entry.createdAt ? formatDate(entry.createdAt, 'datetime') : ''}>
                          <FiClock className="id-inline-icon" /> {entry.createdAt ? getRelativeTime(entry.createdAt) : ''}
                        </span>
                      </span>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </div>
      </div>

      <CancelInvoiceModal
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        invoice={pi}
        onCancelled={afterChange}
        cancel={proformaInvoiceService.cancel}
        noun="proforma invoice"
        description={PI_CANCEL_DESCRIPTION}
      />

      <ConfirmDialog
        isOpen={confirmIssue}
        onClose={() => setConfirmIssue(false)}
        onConfirm={doIssue}
        title="Issue this proforma invoice?"
        message={`This assigns the next PI number and locks it for ${formatINR(pi.totalAmount)}. To revise it later, duplicate it as a new draft.`}
        confirmText="Issue PI"
        variant="primary"
        loading={busy === 'issue'}
      />
      <ConfirmDialog
        isOpen={confirmReinstate}
        onClose={() => setConfirmReinstate(false)}
        onConfirm={doReinstate}
        title={`Reinstate ${pi.number ?? ''}?`}
        message="This undoes the cancellation. The proforma invoice goes back to issued with the same number."
        confirmText="Reinstate"
        variant="primary"
        loading={busy === 'reinstate'}
      />
      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={doDelete}
        title={isDraft ? 'Delete draft' : `Delete ${pi.number}`}
        message={
          isDraft
            ? 'Delete this draft proforma invoice? This cannot be undone.'
            : `Delete cancelled proforma invoice ${pi.number}? It disappears from the list and can no longer be reinstated. Its number is not reused.`
        }
        confirmText={isDraft ? 'Delete draft' : 'Delete'}
        loading={busy === 'delete'}
      />
    </div>
  );
};

export default ProformaInvoiceDetailPage;
